<template>
  <div class="site-footer critical-component-clear">
    <div class="site-footer__row grid container">
      <accordion-snippet
        block-class="site-footer"
        class="col xs-span site-footer__mobile"
        heading-type="h2"
        :panel-data="linkListAccordion"
        title-class="text-body-s-bold"
      />
      <div
        v-if="liquid.showSocialLinks"
        class="col xs-span l3 site-footer__mobile"
      >
        <h2
          class="site-footer__social-text text-body-s-bold"
          v-text="$string('general.site_footer.social_media')"
        />

        <div v-html="liquid.socialLinks" />
      </div>

      <div
        v-for="(block, index) of liquid.blocks"
        :key="`${block.title}-${index}`"
        class="col xs-span l3 site-footer__desktop"
      >
        <h2
          class="site-footer__linklist-title text-body-s-bold"
          v-text="block.title"
        />

        <ul class="site-footer__linklist">
          <li
            v-for="link of block.linkList"
            :key="link.title"
          >
            <a
              :aria-current="link.active"
              class="site-footer__link text-body-s-regular"
              :href="link.url"
              v-text="link.title"
            />
          </li>
        </ul>

        <template v-if="index === 0 && liquid.showSocialLinks">
          <h2
            class="site-footer__social-text text-body-s-bold"
            v-text="$string('general.site_footer.social_media')"
          />

          <div v-html="liquid.socialLinks" />
        </template>
      </div>
      <div class="col xs-span l8-12 site-footer__box">
        <div
          v-if="liquid.collectorsClub.title && liquid.collectorsClub.url"
          class="site-footer__collectors stretched-link-container"
        >
          <icon-collectors />
          <div class="site-footer__collectors--content">
            <btn
              class="site-footer__collectors--button stretched-link"
              :label="liquid.collectorsClub?.title"
              modifiers="text"
              :url="liquid.collectorsClub?.url"
            />
            <p
              class="text-body-xs-regular"
              v-text="liquid.collectorsClub?.description"
            />
          </div>
        </div>
        <a
          v-for="tab of liquid.tabs"
          :key="tab.title"
          class="site-footer__tabs"
          :href="tab.url"
        >
          <p
            class="text-heading-m"
            v-text="tab.title"
          />
          <icon-chevron-right />
        </a>
      </div>
    </div>
  </div>
  <div class="site-footer__row bottom">
    <div class="grid container">
      <div class="site-footer__legal col xs-span l8">
        <p class="site-footer__copyright text-body-xs-regular">
          <span v-html="liquid.copyright" />
        </p>

        <ul class="site-footer__linklist site-footer__linklist--horizontal">
          <li
            v-for="link of liquid.policyMenu"
            :key="link.title"
            class="text-body-xs-regular"
          >
            <a
              :aria-current="link.active"
              class="site-footer__link text-body-xs-regular"
              :href="link.url"
              v-text="link.title"
            />
          </li>
        </ul>
      </div>

      <div
        class="site-footer__payment-icons-container col xs-span l9-12"
        v-html="liquid.paymentIcons"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer (site-footer)
 * -----------------------------------------------------------------------------
 * Site footer component with social links.
 * - Dynamic section.
 *
 * @param {Object} liquid - Formatted data from Shopify.
 * @param {Array} liquid.blocks - Menu linklists.
 * @param {Object} liquid.collectorsClub - Collectors club data.
 * @param {Array} liquid.tabs - Menu tabs.
 * @param {String} liquid.showSocialLinks - Show social links.
 * @param {String} liquid.socialLinks - Captured social media icons snippet.
 * @param {String} liquid.copyright - Copyright string.
 * @param {Array} liquid.policyMenu - Policy linklist.
 * @param {String} liquid.paymentIcons - Captured payment icons snippet.
 * @param {String} liquid.sectionId - Shopify section ID.
 *
 */

import Btn from '~global/btn/btn.vue'
import AccordionSnippet from '~async/accordion-snippet/accordion-snippet'

import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'
import IconCollectors from '~icons/general/collectors.svg'

export default {
  name: 'SiteFooter',

  components: {
    AccordionSnippet,
    Btn,
    IconChevronRight,
    IconCollectors,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        blocks: [],
        collectorsClub: {
          title: '',
          description: '',
          url: '',
        },
        tabs: [],
        showSocialLinks: '',
        socialLinks: '',
        copyright: '',
        policyMenu: [],
        paymentIcons: '',
        sectionId: '',
      }),
    },
  },

  computed: {

    /**
     * Return formatted HTML of footer linklists for accordion.
     * @returns {String}
     */
    linkListAccordion() {
      return this.liquid.blocks.map((block) => {
        const formattedBody = `
          <ul class="site-footer__linklist">
            ${this.getLinklistHTML(block.linkList)}
          </ul>
        `

        return {
          title: block.title,
          content: formattedBody,
        }
      })
    },
  },

  methods: {

    /**
     * Return a formatted series of `<li>` elements containing anchor links.
     * @param {Array} linklist - Footer linklist.
     * @returns {String}
     */
    getLinklistHTML(linklist) {
      return linklist.map((link) => {
        return `
          <li>
            <a
              aria-current="${link.active}"
              class="text-body-s-regular site-footer__link"
              href="${link.url}"
            >
              ${link.title}
            </a>
          </li>
        `
      }).join('')
    },
  },
}
</script>

<style lang="scss">
@import './site-footer';
</style>
